import { cn } from '@heidi/utils';
import type { ReactNode } from 'react';

/**
 * `always` keeps a response's action row on screen; `hover` reserves its space
 * but only shows the actions while the pointer is over (or focus is within) the
 * turn that owns it.
 */
export type ResponseActionsVisibility = 'always' | 'hover';

/** How action rows on EARLIER Work responses behave; the latest is always visible.
 *  Flip to `'always'` to show every row permanently. */
export const EARLIER_RESPONSE_ACTIONS_VISIBILITY: ResponseActionsVisibility = 'hover';

/** Named Tailwind group the owning turn wrapper carries; `hover` reveals on it. */
export const RESPONSE_ACTIONS_HOVER_GROUP_CLASS_NAME = 'group/response';

// Actions opt in with `data-response-action`; `data-response-action-pinned`
// keeps one visible regardless of hover (selected thumb, open feedback popover).
// Hidden only where the device can hover; touch keeps every row visible.
// The reveal selectors out-rank the hide selector (group :hover / :focus-within
// adds specificity), so no ordering tricks are needed.
const HOVER_REVEAL_CLASS_NAME = cn(
  '[&_[data-response-action]]:transition-opacity [&_[data-response-action]]:duration-200 [&_[data-response-action]]:[transition-timing-function:ease]',
  'motion-reduce:[&_[data-response-action]]:transition-none',
  '[@media(hover:hover)]:[&_[data-response-action]:not([data-response-action-pinned])]:opacity-0',
  'group-hover/response:[&_[data-response-action]]:opacity-100',
  'group-focus-within/response:[&_[data-response-action]]:opacity-100'
);

export interface ResponseActionsRevealProps {
  visibility: ResponseActionsVisibility;
  children: ReactNode;
  className?: string;
}

export const ResponseActionsReveal = ({
  visibility,
  children,
  className,
}: ResponseActionsRevealProps) => (
  <div
    className={cn(visibility === 'hover' && HOVER_REVEAL_CLASS_NAME, className)}
    data-response-actions-visibility={visibility}
    data-testid="response-actions-reveal"
  >
    {children}
  </div>
);
